/**
 * Report constants and geometry, ported verbatim from app/public/dashboard.js.
 *
 * Copied rather than imported: the vanilla client is deleted at cutover, and
 * a React build reaching into app/public would break the day it goes. Every
 * value here is the legacy one unchanged; the parity harness
 * (app/scripts/parity) diffs the rendered result against legacy, so drift
 * shows up as a failing check rather than a quiet visual change.
 */

/**
 * Band colours, §1.2, extended to satisfy §1.4.1's own stated principle:
 * "pale band colours never carry text at their own value".
 *
 *   fill  -- the pill background only, where dark text sits ON the colour
 *   chart -- filled marks drawn on white: wedges, ring, dots, markers.
 *            Foundation darkens to the design's own AI-bar value, because
 *            #DCEEEA reads 1.20:1 on white and 1.11:1 against the unfilled
 *            ring — a Foundation gauge was effectively blank.
 *   edge  -- strokes and any text on white. Every value clears 3:1, so a
 *            pale wedge is delineated even where its fill is quiet.
 *
 * Established's edge is darkened from #80B038 (2.57:1, fails) to #5F8429
 * (4.36:1). Same hue family, passes for large text.
 */
export const BAND = {
  Foundation:  { fill: '#DCEEEA', on: '#26443A', chart: '#A6C9C2', edge: '#3F8478', bar: '#A6C9C2' },
  Developing:  { fill: '#88C8C8', on: '#FFFFFF', chart: '#88C8C8', edge: '#3F8478', bar: '#88C8C8' },
  Established: { fill: '#80B038', on: '#17330A', chart: '#80B038', edge: '#5F8429', bar: '#80B038' },
  Advanced:    { fill: '#006443', on: '#FFFFFF', chart: '#006443', edge: '#006443', bar: '#006443' },
};

/* The three swoosh paths, lifted verbatim from the export — Fáilte Ireland
   brand vector art (Brand Guidelines Jan 2023), normalised to unit radius. */
export const SWOOSHES = [
  { fill: '#B5C428', d: 'M-0.6494,-0.6133 C-0.8385,-0.3699 -0.8180,-0.0257 -0.6424,0.2380 C-0.6421,0.2384 -0.6419,0.2388 -0.6416,0.2391 C-0.6419,0.2385 -0.6422,0.2379 -0.6425,0.2372 C-0.6638,-0.0711 -0.5307,-0.4659 -0.2137,-0.6457 C-0.0333,-0.7480 0.1684,-0.7761 0.3554,-0.7400 C0.4709,-0.7178 0.5805,-0.6709 0.6762,-0.6019 C0.4819,-0.8430 0.1774,-0.9385 -0.1218,-0.8918 C-0.3179,-0.8601 -0.5239,-0.7749 -0.6494,-0.6133 Z' },
  { fill: '#70CACB', d: 'M-0.2020,0.8965 C0.1034,0.9385 0.3911,0.7487 0.5317,0.4646 C0.5320,0.4642 0.5321,0.4638 0.5323,0.4634 C0.5320,0.4639 0.5316,0.4646 0.5312,0.4651 C0.2748,0.6378 -0.1336,0.7200 -0.4479,0.5354 C-0.6267,0.4304 -0.7520,0.2698 -0.8143,0.0897 C-0.8527,-0.0214 -0.8670,-0.1397 -0.8550,-0.2571 C-0.9666,0.0318 -0.8970,0.3432 -0.7069,0.5790 C-0.5815,0.7328 -0.4048,0.8687 -0.2020,0.8965 Z' },
  { fill: '#62BB46', d: 'M0.8874,-0.2512 C0.7683,-0.5354 0.4586,-0.6867 0.1424,-0.6633 C0.1419,-0.6633 0.1415,-0.6633 0.1410,-0.6633 C0.1418,-0.6631 0.1424,-0.6631 0.1432,-0.6630 C0.4222,-0.5302 0.7007,-0.2202 0.7015,0.1442 C0.7021,0.3516 0.6274,0.5412 0.5042,0.6863 C0.4279,0.7758 0.3334,0.8485 0.2262,0.8978 C0.5317,0.8469 0.7644,0.6287 0.8708,0.3451 C0.9395,0.1588 0.9666,-0.0624 0.8874,-0.2512 Z' },
];

export const SWIRL_N = 96;
export const SWIRL_TWIST = 52;

/* Short labels for the coxcomb rim, where 66px of width is all there is.
   Full canonical names come from result.dimensionLabels and are used in the
   tooltip and the gap chart. */
export const SHORT = { d1: 'Digital', d2: 'Ops', d3: 'Visitor', d4: 'Data', d5: 'AI' };
export const KEYS = ['d1', 'd2', 'd3', 'd4', 'd5'];

export const CAPABILITIES = [
  ['awareness', 'AI Awareness & Adoption'],
  ['literacy', 'AI Literacy & Critical Evaluation'],
  ['responsibleUse', 'Responsible AI Use'],
  ['dataReadiness', 'Data Readiness for AI'],
  ['processReadiness', 'Process Readiness for AI'],
  ['strategicIntent', 'Strategic AI Intent'],
];

/* Plain-English framing per band. Deliberately describes the state, never
   arithmetic — app/README.md forbids per-question point language. */
export const READINESS_COPY = {
  leadership: {
    Foundation: 'Digital is not yet a leadership priority, and no plan or budget is in place.',
    Developing: 'Leadership sees the need but has not yet committed a plan or a sustained budget.',
    Established: 'Leadership is actively working towards a clear digital direction with budget behind it.',
    Advanced: 'Digital is central to strategy, with a documented plan and KPIs reviewed regularly.',
  },
  team: {
    Foundation: 'The team has little time, confidence or capacity for digital work.',
    Developing: 'There is willingness to try new tools, but time and confidence are inconsistent.',
    Established: 'The team is comfortable with the core tools and has regular time for digital work.',
    Advanced: 'Digital is properly resourced, with confident users and clear ownership.',
  },
};

/**
 * The EU Digital Intensity Index — 12 binary variables, 9 asked directly and
 * 3 derived from the maturity answers.
 *
 * Bands are Eurostat's integer buckets (0–3 / 4–6 / 7–9 / 10–12), NOT the
 * design spec §6.2 half-open intervals (0–3 / 3–6 / 6–9 / 9–12), which band
 * a score of 3 as Low where Eurostat calls it Very low. The score is a count
 * of variables, so the buckets are integers by construction. Fill colours
 * are the spec's, unchanged.
 *
 * The §6.3 needle gauge is deliberately not built — see app/README.md.
 */
export const DII_BANDS = [
  { band: 'Very low', min: 0, max: 3, fill: '#DCEEEA', on: '#26443A', edge: '#3F8478' },
  { band: 'Low', min: 4, max: 6, fill: '#88C8C8', on: '#0B3B34', edge: '#3F8478' },
  { band: 'High', min: 7, max: 9, fill: '#80B038', on: '#17330A', edge: '#5F8429' },
  { band: 'Very high', min: 10, max: 12, fill: '#006443', on: '#FFFFFF', edge: '#006443' },
];

/** The 12 variables, in the order Eurostat lists them. */
export const DII_VARIABLES = [
  { key: 'internetAccess', label: 'Internet access for over half of staff', source: 'D-DII-01' },
  { key: 'broadband30Mbps', label: 'Fixed broadband of at least 30 Mb/s', source: 'D-DII-02' },
  { key: 'website', label: 'Has a website', source: 'D1-01', derived: true },
  { key: 'socialMedia', label: 'Uses social media', source: 'D1-03', derived: true },
  { key: 'onlineSales', label: 'Online sales worth 1% or more of turnover', source: 'D-DII-03' },
  { key: 'b2cShare', label: 'Over 10% of online sales direct to consumers', source: 'D-DII-04' },
  { key: 'cloudServices', label: 'Buys cloud computing services', source: 'D-DII-05' },
  { key: 'advancedCloud', label: 'Uses sophisticated or intermediate cloud', source: 'D-DII-05a' },
  { key: 'ictSpecialists', label: 'Employs an ICT specialist', source: 'D-DII-06' },
  { key: 'erp', label: 'Uses ERP software', source: 'D-DII-07' },
  { key: 'crm', label: 'Uses CRM software', source: 'D-DII-08' },
  { key: 'aiUse', label: 'Uses AI', source: 'D5-AI-01', derived: true },
];

export const diiBand = (score) => DII_BANDS.find((b) => score >= b.min && score <= b.max) ?? DII_BANDS[0];

export const point = (cx, cy, r, deg) => {
  const a = (deg * Math.PI) / 180;
  return { x: cx + r * Math.cos(a), y: cy + r * Math.sin(a) };
};

/* Annular wedge — an inner hub keeps both radial edges true straight lines
   instead of collapsing to a shared apex. Verbatim from the export. */
export function wedgePath(cx, cy, r, a0, a1, hub) {
  const f = (v) => v.toFixed(3);
  const o0 = point(cx, cy, r, a0);
  const o1 = point(cx, cy, r, a1);
  if (!hub || hub <= 0.01) {
    return `M${f(cx)},${f(cy)} L${f(o0.x)},${f(o0.y)} A${f(r)},${f(r)} 0 0,1 ${f(o1.x)},${f(o1.y)} Z`;
  }
  const i0 = point(cx, cy, hub, a0);
  const i1 = point(cx, cy, hub, a1);
  return `M${f(i0.x)},${f(i0.y)} L${f(o0.x)},${f(o0.y)} A${f(r)},${f(r)} 0 0,1 ${f(o1.x)},${f(o1.y)} `
       + `L${f(i1.x)},${f(i1.y)} A${f(hub)},${f(hub)} 0 0,0 ${f(i0.x)},${f(i0.y)} Z`;
}

/** Score to percentage — the same (score - 1) / 3 normalisation the engine uses. */
export const pctOf = (score) => ((score - 1) / 3) * 100;

/**
 * Why no sector comparison is shown.
 *
 * Declining consent removes every comparison on the report at once — the
 * average bars, the delta line, the gap-chart benchmark and the AI cohort
 * ticks. That is the agreed behaviour (Build Log 2026-08-19: a No respondent
 * sees their own chart with no sector-comparison option present at all), but
 * an unexplained absence reads as four broken panels rather than a respected
 * choice. Always say why.
 */
export function comparisonAbsenceReason(report, benchmark) {
  if (!report.consented) {
    return 'No sector comparison — you chose not to share your data for sector insights.';
  }
  if (benchmark?.peers && !benchmark.peers.available) {
    return `Sector comparison needs at least ${benchmark.peers.minCellSize} similar businesses; `
      + `there ${benchmark.peers.count === 1 ? 'is' : 'are'} ${benchmark.peers.count} so far.`;
  }
  return 'Sector comparison will appear once more businesses complete the assessment.';
}
